// apps/web/src/pages/admin/RecoveryCompaniesPage.tsx

import React, { useState } from 'react';
import { trpc } from '@/trpc/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  Truck, Search, ChevronLeft, ChevronRight, Loader2, Edit, Trash2, 
  Plus, MapPin, Phone, Mail, CheckCircle, XCircle, Terminal, Info as InfoIcon 
} from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { 
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, 
  DialogFooter, DialogTrigger 
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { District } from '@logistics-platform/types';
import MapPicker from '@/components/MapPicker';

interface RecoveryCompany {
  id: string;
  name: string;
  phone: string;
  phoneTwo?: string | null;
  email?: string | null;
  address?: string | null;
  district: District;
  latitude?: number | null;
  longitude?: number | null;
  isActive: boolean;
  services: string[];
  description?: string | null;
  createdAt: string;
  updatedAt: string;
}

const RecoveryCompaniesPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState<District | undefined>(undefined);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { data: districts } = trpc.emergencyService.getDistricts.useQuery();
  
  const { data, isLoading, error, refetch } = trpc.emergencyService.getAllRecoveryCompanies.useQuery({
    page,
    pageSize,
    search: debouncedSearchQuery,
    district: selectedDistrict,
  }, {
    refetchInterval: 5000
  });

  const companies = data?.companies || [];
  const totalCompanies = data?.totalCount || 0;
  const totalPages = Math.ceil(totalCompanies / pageSize);

  // Create mutation
  const createMutation = trpc.emergencyService.createRecoveryCompany.useMutation({
    onSuccess: () => { toast.success("Recovery company created!"); refetch(); setIsCreateModalOpen(false); resetForm(); },
    onError: (err) => toast.error(err.message),
  });

  // Update mutation
  const updateMutation = trpc.emergencyService.updateRecoveryCompany.useMutation({
    onSuccess: () => { toast.success("Recovery company updated!"); refetch(); setIsEditModalOpen(false); setEditingCompany(null); },
    onError: (err) => toast.error(err.message),
  });

  // Delete mutation
  const deleteMutation = trpc.emergencyService.deleteRecoveryCompany.useMutation({
    onSuccess: () => { toast.success("Recovery company deleted!"); refetch(); },
    onError: (err) => toast.error(err.message),
  });

  // Form state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMapPickerOpen, setIsMapPickerOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<RecoveryCompany | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    phoneTwo: '',
    email: '',
    address: '',
    district: District.Lilongwe,
    latitude: '',
    longitude: '',
    services: [] as string[],
    description: '',
  });

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      phoneTwo: '',
      email: '',
      address: '',
      district: District.Lilongwe,
      latitude: '',
      longitude: '',
      services: [],
      description: '',
    });
  };

  const handleCreate = () => {
    createMutation.mutate({
      ...formData,
      latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
      longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
    });
  };

  const handleUpdate = () => {
    if (!editingCompany) return;
    
    updateMutation.mutate({
      id: editingCompany.id,
      name: formData.name || undefined,
      phone: formData.phone || undefined,
      phoneTwo: formData.phoneTwo || null,
      email: formData.email || null,
      address: formData.address || null,
      district: formData.district,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      services: formData.services,
      description: formData.description || null,
    });
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this recovery company?')) {
      deleteMutation.mutate({ id });
    }
  };

  const openEditModal = (company: RecoveryCompany) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      phone: company.phone,
      phoneTwo: company.phoneTwo || '',
      email: company.email || '',
      address: company.address || '',
      district: company.district,
      latitude: company.latitude?.toString() || '',
      longitude: company.longitude?.toString() || '',
      services: company.services,
      description: company.description || '',
    });
    setIsEditModalOpen(true);
  };

  const availableServices = [
    'TOWING', 'BATTERY_JUMP', 'TIRE_CHANGE', 'FUEL_DELIVERY', 
    'LOCKOUT_SERVICE', 'ACCIDENT_RECOVERY', 'WINCHING', 'FLATBED_TOWING'
  ];

  if (error) {
    return (
      <div className="container mx-auto py-12 px-4 max-w-7xl">
        <Alert variant="destructive">
          <Terminal className="h-4 w-4" />
          <AlertTitle>Error Loading Recovery Companies</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-4 px-2 sm:py-12 sm:px-4 max-w-full animate-fade-in">
      <Card className="glass">
        <CardHeader className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-foreground text-lg sm:text-2xl">
                <Truck className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                Recovery Companies
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm mt-1">
                Manage vehicle recovery companies that appear when drivers trigger SOS.
              </CardDescription>
            </div>
            <Button onClick={() => { resetForm(); setIsCreateModalOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Add Company
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6 pt-0 sm:pt-0">
          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-3 sm:gap-4">
            <Input
              placeholder="Search by name, phone, or address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full md:flex-1 bg-input text-foreground h-9 sm:h-10 text-sm"
            />
            <Select value={selectedDistrict} onValueChange={(value: District) => setSelectedDistrict(value)}>
              <SelectTrigger className="w-full md:w-[200px] bg-select text-foreground h-9 sm:h-10 text-xs sm:text-sm">
                <SelectValue placeholder="Filter by District" />
              </SelectTrigger>
              <SelectContent className="bg-popover text-popover-foreground">
                <SelectItem value={undefined as any}>All Districts</SelectItem>
                {districts?.map(district => <SelectItem key={district} value={district}>{district}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="h-9 sm:h-10" onClick={() => { setSearchQuery(''); setSelectedDistrict(undefined); }}>
              Reset
            </Button>
          </div>

          {/* Companies Table */}
          <div className="rounded-md border bg-card text-card-foreground shadow-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px] text-primary">Name</TableHead>
                  <TableHead className="w-[150px] text-primary">Contact</TableHead>
                  <TableHead className="w-[120px] text-primary">District</TableHead>
                  <TableHead className="w-[150px] text-primary">Services</TableHead>
                  <TableHead className="w-[100px] text-primary">Status</TableHead>
                  <TableHead className="w-[120px] text-primary">Added</TableHead>
                  <TableHead className="w-[150px] text-primary text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: pageSize }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={7}>
                        <Skeleton className="h-8 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : companies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                      <p><InfoIcon className="inline-block mr-2 h-5 w-5" /> No recovery companies found.</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  companies.map((company) => (
                    <TableRow key={company.id} className="text-sm">
                      <TableCell>
                        <p className="font-medium text-foreground">{company.name}</p>
                        {company.description && (
                          <p className="text-muted-foreground text-xs truncate max-w-[200px]">{company.description}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="flex items-center gap-1 text-xs">
                            <Phone className="h-3 w-3" />
                            {company.phone}
                          </p>
                          {company.phoneTwo && (
                            <p className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Phone className="h-3 w-3" />
                              {company.phoneTwo}
                            </p>
                          )}
                          {company.email && (
                            <p className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Mail className="h-3 w-3" />
                              {company.email}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{company.district}</Badge>
                        {company.address && (
                          <p className="text-xs text-muted-foreground mt-1 truncate max-w-[150px]">{company.address}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {company.services.slice(0, 2).map(service => (
                            <Badge key={service} variant="secondary" className="text-xs">{service}</Badge>
                          ))}
                          {company.services.length > 2 && (
                            <Badge variant="secondary" className="text-xs">+{company.services.length - 2}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={company.isActive ? "success" : "destructive"}>
                          {company.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {format(new Date(company.createdAt), 'yyyy-MM-dd')}
                      </TableCell>
                      <TableCell className="flex justify-center items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEditModal(company)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => handleDelete(company.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center pt-4">
            <p className="text-sm text-muted-foreground">
              Showing {Math.min(totalCompanies, (page - 1) * pageSize + 1)} - {Math.min(totalCompanies, page * pageSize)} of {totalCompanies} companies
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4" /> Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(prev => prev + 1)}
                disabled={page >= totalPages}
              >
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-xl bg-card text-card-foreground max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">Add Recovery Company</DialogTitle>
            <DialogDescription>
              Enter details for the new vehicle recovery company.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">Name *</Label>
              <Input 
                id="name" 
                value={formData.name} 
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="phone" className="text-right">Phone *</Label>
              <Input 
                id="phone" 
                value={formData.phone} 
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="phoneTwo" className="text-right">Phone 2</Label>
              <Input 
                id="phoneTwo" 
                value={formData.phoneTwo} 
                onChange={(e) => setFormData({...formData, phoneTwo: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">Email</Label>
              <Input 
                id="email" 
                type="email"
                value={formData.email} 
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="district" className="text-right">District *</Label>
              <Select 
                value={formData.district} 
                onValueChange={(value: District) => setFormData({...formData, district: value})}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {districts?.map(district => <SelectItem key={district} value={district}>{district}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="address" className="text-right">Address</Label>
              <Input 
                id="address" 
                value={formData.address} 
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">Location</Label>
              <div className="col-span-3 flex gap-2">
                <Input 
                  id="latitude" 
                  type="number"
                  step="any"
                  placeholder="Latitude"
                  value={formData.latitude} 
                  onChange={(e) => setFormData({...formData, latitude: e.target.value})}
                  className="flex-1" 
                />
                <Input 
                  id="longitude" 
                  type="number"
                  step="any"
                  placeholder="Longitude"
                  value={formData.longitude} 
                  onChange={(e) => setFormData({...formData, longitude: e.target.value})}
                  className="flex-1" 
                />
                <Button 
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setIsMapPickerOpen(true)}
                  title="Pick on map"
                >
                  <MapPin className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label className="text-right pt-2">Services</Label>
              <div className="col-span-3 flex flex-wrap gap-2">
                {availableServices.map(service => (
                  <Badge 
                    key={service}
                    variant={formData.services.includes(service) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        services: prev.services.includes(service) 
                          ? prev.services.filter(s => s !== service)
                          : [...prev.services, service]
                      }));
                    }}
                  >
                    {service.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="description" className="text-right pt-2">Description</Label>
              <textarea 
                id="description" 
                value={formData.description} 
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="col-span-3 min-h-[80px] p-2 rounded-md border bg-background text-foreground"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={createMutation.isLoading || !formData.name || !formData.phone}>
              {createMutation.isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Create Company'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Map Picker */}
      <MapPicker
        isOpen={isMapPickerOpen}
        onClose={() => setIsMapPickerOpen(false)}
        onSelect={(lat, lng) => {
          setFormData(prev => ({
            ...prev,
            latitude: lat.toString(),
            longitude: lng.toString()
          }));
        }}
        initialLat={formData.latitude ? parseFloat(formData.latitude) : undefined}
        initialLng={formData.longitude ? parseFloat(formData.longitude) : undefined}
      />

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-xl bg-card text-card-foreground max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">Edit Recovery Company</DialogTitle>
            <DialogDescription>
              Update details for {editingCompany?.name}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-name" className="text-right">Name</Label>
              <Input 
                id="edit-name" 
                value={formData.name} 
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-phone" className="text-right">Phone</Label>
              <Input 
                id="edit-phone" 
                value={formData.phone} 
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-phoneTwo" className="text-right">Phone 2</Label>
              <Input 
                id="edit-phoneTwo" 
                value={formData.phoneTwo} 
                onChange={(e) => setFormData({...formData, phoneTwo: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-email" className="text-right">Email</Label>
              <Input 
                id="edit-email" 
                type="email"
                value={formData.email} 
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-district" className="text-right">District</Label>
              <Select 
                value={formData.district} 
                onValueChange={(value: District) => setFormData({...formData, district: value})}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {districts?.map(district => <SelectItem key={district} value={district}>{district}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-address" className="text-right">Address</Label>
              <Input 
                id="edit-address" 
                value={formData.address} 
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">Location</Label>
              <div className="col-span-3 flex gap-2">
                <Input 
                  id="edit-latitude" 
                  type="number"
                  step="any"
                  placeholder="Latitude"
                  value={formData.latitude} 
                  onChange={(e) => setFormData({...formData, latitude: e.target.value})}
                  className="flex-1" 
                />
                <Input 
                  id="edit-longitude" 
                  type="number"
                  step="any"
                  placeholder="Longitude"
                  value={formData.longitude} 
                  onChange={(e) => setFormData({...formData, longitude: e.target.value})}
                  className="flex-1" 
                />
                <Button 
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setIsMapPickerOpen(true)}
                  title="Pick on map"
                >
                  <MapPin className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">Active</Label>
              <div className="col-span-3 flex items-center gap-2">
                <Button
                  variant={editingCompany?.isActive ? "default" : "outline"}
                  size="sm"
                  onClick={() => editingCompany && updateMutation.mutate({ id: editingCompany.id, isActive: true })}
                >
                  <CheckCircle className="h-4 w-4 mr-1" /> Active
                </Button>
                <Button
                  variant={!editingCompany?.isActive ? "destructive" : "outline"}
                  size="sm"
                  onClick={() => editingCompany && updateMutation.mutate({ id: editingCompany.id, isActive: false })}
                >
                  <XCircle className="h-4 w-4 mr-1" /> Inactive
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label className="text-right pt-2">Services</Label>
              <div className="col-span-3 flex flex-wrap gap-2">
                {availableServices.map(service => (
                  <Badge 
                    key={service}
                    variant={formData.services.includes(service) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        services: prev.services.includes(service) 
                          ? prev.services.filter(s => s !== service)
                          : [...prev.services, service]
                      }));
                    }}
                  >
                    {service.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="edit-description" className="text-right pt-2">Description</Label>
              <textarea 
                id="edit-description" 
                value={formData.description} 
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="col-span-3 min-h-[80px] p-2 rounded-md border bg-background text-foreground"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isLoading}>
              {updateMutation.isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecoveryCompaniesPage;
